import type { VoidFunctionComponent } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { LabeledToggle } from '../../generic/Forms/LabeledToggle';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import PaidPill from '../../generic/Pill/PaidPill';

import Section from './SettingsPageSection';

import css from './Settings.module.less';

type VendoredDependencyScanningSettingsSectionProps = {
  /** Current state of vendored dependency scanning */
  isVendoredScanningEnabled: boolean;
  /** Callback when checkbox is toggled */
  onToggleVendoredScanning: (isChecked: boolean) => void;
  /** Whether main scanning is enabled */
  isScanningEnabled: boolean;
  /** User permission to edit policy */
  canEditPolicy: boolean;
};

/**
 * VendoredDependencyScanningSettingsSection
 *
 * Renders a toggle control for enabling/disabling vendored dependency issue scanning.
 * When the VENDORED_DEPENDENCY_DETECTION feature flag is not enabled, the toggle
 * is displayed grayed out with a paid pill indicator.
 */
const VendoredDependencyScanningSettingsSection: VoidFunctionComponent<
  VendoredDependencyScanningSettingsSectionProps
> = ({
  isVendoredScanningEnabled,
  onToggleVendoredScanning,
  isScanningEnabled,
  canEditPolicy,
}) => {
  const isVendoredDetectionEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const isRestricted = !isVendoredDetectionEnabled;
  const restrictedClass = isRestricted ? css.issueSectionDisabled : undefined;

  return (
    <PaidFeatureGate isRestricted={isRestricted} hidePill useReadOnly={false}>
      <hr />
      <LabeledToggle
        label={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span className={restrictedClass}>
              Scan for Vendored Dependencies
            </span>
            {isRestricted && <PaidPill />}
          </span>
        }
        isChecked={isVendoredScanningEnabled}
        onChange={(isChecked) => onToggleVendoredScanning(isChecked)}
        isDisabled={!canEditPolicy || !isScanningEnabled || isRestricted}
      />
      <div className={restrictedClass}>
        <Section.Paragraph>
          This will turn on issue creation for Vendored Components.
        </Section.Paragraph>
      </div>
    </PaidFeatureGate>
  );
};

export default VendoredDependencyScanningSettingsSection;
